import React, { memo, useState } from 'react';
import { signIn, signUp, confirmSignUp, getCurrentUser } from 'aws-amplify/auth';
import { isIOS } from '../services/platform';
import { nativeAppleSignIn } from '../services/nativeAppleSignIn';

function AuthScreen({ onAuth, onSkip }) {
  const [mode, setMode] = useState('signin'); // signin, signup, confirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn({ username: email, password });
      const user = await getCurrentUser();
      onAuth(user);
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            preferred_username: displayName || email.split('@')[0],
          },
        },
      });
      setMode('confirm');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApple = async () => {
    setError('');
    setLoading(true);
    try {
      await nativeAppleSignIn();
      const user = await getCurrentUser();
      onAuth(user);
    } catch (err) {
      // The plugin rejects with 'canceled' when the user dismisses the sheet —
      // that's not an error worth surfacing.
      if (err?.message !== 'canceled' && err !== 'canceled') {
        setError(err.message || 'Apple sign in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: confirmCode });
      await signIn({ username: email, password });
      const user = await getCurrentUser();
      onAuth(user);
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h2 className="auth-title">BRAIN GRID</h2>
        <p className="auth-subtitle">
          {mode === 'signin' && 'Sign in to save scores & compete'}
          {mode === 'signup' && 'Create your account'}
          {mode === 'confirm' && 'Enter verification code'}
        </p>

        {error && <div className="auth-error">{error}</div>}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="auth-form">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
              autoComplete="current-password"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Signing in...' : 'SIGN IN'}
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <input
              type="text"
              placeholder="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="auth-input"
              maxLength={20}
              autoComplete="username"
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password (8+ chars, upper + lower + number)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Creating account...' : 'SIGN UP'}
            </button>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className="auth-form">
            <p className="auth-hint">Check {email} for a verification code</p>
            <input
              type="text"
              placeholder="Verification code"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              className="auth-input"
              required
              autoComplete="one-time-code"
            />
            <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'VERIFY'}
            </button>
          </form>
        )}

        {isIOS() && mode !== 'confirm' && (
          <>
            <div className="auth-divider"><span>or</span></div>
            <button
              type="button"
              className="auth-btn auth-btn-apple"
              onClick={handleApple}
              disabled={loading}
              aria-label="Sign in with Apple"
            >
              <svg className="auth-apple-logo" viewBox="0 0 384 512" width="16" height="16" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <span>Sign in with Apple</span>
            </button>
          </>
        )}

        <div className="auth-switch">
          {mode === 'signin' && (
            <button className="auth-btn-link" onClick={() => { setMode('signup'); setError(''); }}>
              Don't have an account? Sign up
            </button>
          )}
          {(mode === 'signup' || mode === 'confirm') && (
            <button className="auth-btn-link" onClick={() => { setMode('signin'); setError(''); }}>
              Already have an account? Sign in
            </button>
          )}
        </div>

        <button className="auth-btn auth-btn-ghost" onClick={onSkip}>
          PLAY AS GUEST
        </button>
      </div>
    </div>
  );
}

export default memo(AuthScreen);
